
import { useEffect, useState, useRef } from "react";
import { Column, ColumnEditorOptions } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useTranslation } from "react-i18next";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import TypeService from "../../services/complaintType/type";
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";


const TypeDatatable: React.FC<{ complaintTypes: any[] }> = ({ complaintTypes }) => {
  const { t } = useTranslation();
  const [complaintTypess, setTypes] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const toast = useRef<Toast>(null);


  useEffect(() => {
    fetchType();
  }, []);

  useEffect(() => {
    setTypes(complaintTypes); // Sync with parent props
  }, [complaintTypes]);

  const fetchType = async () => {
    try {
      const response = await TypeService.getComplaintType();
      setTypes(response);
    } catch (error) {
      console.error("Error fetching types:", error);
    }
  };

  const showMessage = (label: string, message: string, severity: any) => {
    toast.current?.show({
      severity: severity,
      summary: label,
      detail: message,
      life: 3000,
    });
  };

  const nameEditor = (options: any) => (
    <InputText
      type="text"
      value={options.value}
      style={{ width: "95%" }}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        options.editorCallback!(e.target.value)
      }
    />
  );

  const subCategoryEditor = (options: any) => (
    <InputText
      type="text"
      value={options.value}
      style={{ width: "95%" }}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        options.editorCallback!(e.target.value)
      }
    />
  );

  const onRowEditComplete = async (event: DataTableRowEditCompleteEvent) => {
    const { newData, index } = event;
    let updatedcomplaintTypes = [...complaintTypess];
      const newName = newData.complaintName?.trim().toLowerCase();
      if (!newName) {
        showMessage("Validation Error", "Complaint type name cannot be empty!", "warn");
        return; 
      }
      const isDuplicate = complaintTypess.some(
        (type, i) =>
          i !== index &&
          type.complaintName?.trim().toLowerCase() === newName
      );

      if (isDuplicate) {
        showMessage("Duplicate Entry", "Complaint type name already exists!", "warn");
        return; 
      }
    if (updatedcomplaintTypes[index] !== newData) {
      updatedcomplaintTypes[index] = newData;
      setTypes(updatedcomplaintTypes);

      try {
        const response = await TypeService.updateComplaintType(newData);
        if (response) {
          showMessage("Update Type", "Type updated successfully!", "success");
          fetchType(); // Refresh data instead of reloading the page
        } else {
          showMessage("Update Type", "Failed to update type.", "error");
        }
      } catch (error) {
        console.error("Error updating type:", error);
        showMessage("Update Type", "Error updating type.", "error");
      }
    }
  };

  const confirmDelete = (rowData: any) => {
    setSelectedRow(rowData);
    confirmDialog({
      message: "Are you sure you want to delete this item?",
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDelete(rowData),
      reject: () => console.log("Delete Cancelled"),
    });
  };

  
  const handleDelete = async (rowData: any) => {

    try {
        const response = await TypeService.deleteComplaintType(rowData.complaintTypeId);

        if (response) {  
            setTypes(prevTypes => prevTypes.filter(type => type.complaintTypeId !== rowData.complaintTypeId));
            showMessage("Delete Type", "Type deleted successfully!", "success");
        } else {
            showMessage("Delete Type", "Failed to delete type. Backend did not confirm deletion.", "error");
        }
    } catch (error) {
        console.error("Error deleting type:", error);
        showMessage("Delete Type", "Error deleting type. Check console logs for details.", "error");
    }
};

  return (
    <div className="col-12">
      <Toast ref={toast} />
      <ConfirmDialog /> {/* PrimeReact Confirmation Dialog */}
      
      <div className="card border-noround p-2">
        <DataTable
          value={complaintTypess}
          paginator
          rows={5}
          stripedRows
          alwaysShowPaginator
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport  NextPageLink LastPageLink "
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          editMode="row"
          onRowEditComplete={onRowEditComplete}
        >
          <Column field="complaintName" header={"Complaint Type"} editor={nameEditor} style={{ width: "25%" }} />
          <Column field="description" header={t("Description")} editor={subCategoryEditor} style={{ width: "25%" }} />
          <Column header={t("Edit")} rowEditor style={{ width: "8%" }} />
          <Column
            header="Delete"
            body={(rowData) => (
              <Button
                icon="pi pi-trash"
                onClick={() => confirmDelete(rowData)}
                className="p-button-rounded p-button-text p-button-danger"
                tooltip="Delete"
              />
            )}
            style={{ width: "8%" }}
          />
        </DataTable>
      </div>
    </div>
  );
};

export default TypeDatatable;

